import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Button } from '@material-ui/core';
import { CloudUpload as CloudUploadIcon } from '@material-ui/icons';

import { isVersionEqual } from '../../../common/version-item/Version.helpers';
import AppSnackbar from '../../../common/AppSnackbar';
import { useUserContext } from '../../../user/User.context';
import DocumentUploadDialog from '../../documents/components/DocumentUploadDialog';
import DocumentVersionReferenceCard from '../../documents/components/DocumentVersionReferenceCard';
import { ReferenceMetadata } from '../../documents/Document.interface';
import {
  useAssignTaskFileVersions,
  useFindTaskFileVersions
} from '../Task.hooks';
import { BaseTaskCardProps } from '../Task.interface';

export interface TaskFileVersionReferenceCardProps extends BaseTaskCardProps {
  disabled?: boolean;
}

const TaskFileVersionReferenceCard: React.FC<
  TaskFileVersionReferenceCardProps
> = props => {
  const { task, disabled, onChange, onError } = props;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState<string>();
  const [success, setSuccess] = useState<{
    open: boolean;
    message?: React.ReactNode;
  }>({ open: false });
  const [direction, setDirection] = useState<'ASC' | 'DESC'>();
  const can = useUserContext().permissions.file;
  const taskId = task.id || 0;
  const {
    files,
    isLoading: isFinding,
    error: fetchError,
    findFiles
  } = useFindTaskFileVersions(taskId);
  const {
    task: updatedTask,
    isLoading: isAssigning,
    error: assignError,
    assign
  } = useAssignTaskFileVersions(taskId);
  const isLoading = isFinding || isAssigning;

  const referenceMetadata: ReferenceMetadata = useMemo(() => {
    return { type: 'task.assignFileVersions', data: { id: taskId } };
  }, [taskId]);

  const findFilesWithParams = useCallback(() => {
    findFiles({ order, direction });
  }, [order, direction, findFiles]);

  useEffect(() => {
    findFilesWithParams();
  }, [findFilesWithParams]);

  useEffect(() => {
    if (updatedTask) {
      setSuccess({ open: true, message: 'Documents assigned successfully' });
      findFilesWithParams();
      onChangeRef.current?.(updatedTask);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (assignError) {
      onErrorRef.current?.(assignError);
    }
  }, [assignError]);

  return (
    <>
      <DocumentVersionReferenceCard
        items={files}
        error={fetchError}
        disabled={disabled}
        direction={direction}
        isLoading={isLoading}
        actions={
          <Button
            color='primary'
            variant='outlined'
            disabled={can.not.update || disabled}
            startIcon={<CloudUploadIcon />}
            onClick={() => setOpen(true)}
          >
            Upload Files
          </Button>
        }
        isActive={column => column === order}
        handleSort={column => {
          setOrder(column);
          setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
        }}
        onRemove={item => {
          const updated = (files || [])
            .filter(file => !isVersionEqual(file, item))
            .map(file => ({ id: file.id, version: file.version }));
          assign(updated);
        }}
        onAssign={items => {
          const updated = items.map(item => {
            return { id: item.id, version: item.version };
          });
          assign(updated);
        }}
      />

      <DocumentUploadDialog
        open={open}
        setOpen={setOpen}
        referenceMetadata={referenceMetadata}
        onDismiss={(progress, shouldFetch = true) => {
          const { success } = progress;
          if (success > 0) {
            setSuccess({
              open: true,
              message: `Number of files uploaded: ${success}`
            });
            shouldFetch && findFilesWithParams();
            onChangeRef.current?.({});
          }
        }}
      />

      <AppSnackbar
        severity='success'
        open={success.open}
        onClose={() => setSuccess(success => ({ ...success, open: false }))}
      >
        {success.message}
      </AppSnackbar>
    </>
  );
};

export default TaskFileVersionReferenceCard;
